import type { AppComboboxOption } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useContacts } from '@inquiries/-hooks/useContacts'
import { useDivisionOptions } from '@inquiries/-hooks/useDivisionOptions'
import { useInquiryRoleMembers } from '@inquiries/-hooks/useInquiryRoleMembers'
import { resolveEffectiveStartOn } from '@inquiries/-lib/effective-start-on'
import { inquiryFormSchema } from '@inquiries/-lib/inquiryValidation'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DEFAULT_WORK_LOCATIONS,
  type InquiryPriority,
  type InquiryRecruitmentType,
  SALESFORCE_PROJECT_MEMBER_TYPE_IDS,
  VALIDATION_MESSAGES,
  type WorkLocations,
} from 'shared'
import type { SalesforceDeal } from '@/lib'
import { getVisibleErrors, touchAll, validateForm } from '@/lib/form-validation'
import { useSalesforceProjectMembers } from '@/shared-hooks/useSalesforceProjectMembers'
import { useCreateInquiry } from './useCreateInquiry'
import { useSalesforceCustomers } from './useSalesforceCustomers'
import { useSalesforceDealAnos } from './useSalesforceDealAnos'
import { useSalesforceDeals } from './useSalesforceDeals'

type Step = 'select-deal' | 'input-details'

type SelectedDeal = {
  id: number
  salesforce_deal_name: string
  salesforce_customer_id: number
  customer_name: string
}

export function useCreateDealDialog(onOpenChange: (open: boolean) => void) {
  // Step管理
  const [step, setStep] = useState<Step>('select-deal')

  // 顧客選択
  // 顧客は数万件規模あるため AppCombobox のクライアント側フィルタは使わず、
  // 「検索バーに入力 → 入力停止から 300ms 後に API 発火 → セレクトに候補を表示 → 選択」の UI とする。
  const [customerId, setCustomerId] = useState('')
  // 顧客名（selectedDeal とは独立して保持）。検索結果は入力のたびに入れ替わるため、
  // 選択時の名前を state で保持する（後続ステップの表示に使う）。
  const [customerName, setCustomerName] = useState('')
  const [customerSearchInput, setCustomerSearchInput] = useState('')
  const [customerQuery, setCustomerQuery] = useState('')

  const { data: customersData, isFetching: isFetchingCustomers } = useSalesforceCustomers(customerQuery)

  // 商談選択
  const salesforceCustomerId = customerId ? Number(customerId) : undefined
  const [dealName, setDealName] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [selectedDeal, setSelectedDeal] = useState<SelectedDeal | null>(null)

  const { data: dealsData } = useSalesforceDeals(salesforceCustomerId)

  const dealResults = useMemo(
    () =>
      dealsData?.salesforce_deals.filter((deal) => {
        if (!dealName) return true
        return deal.salesforce_deal_name.includes(dealName)
      }) ?? [],
    [dealsData, dealName],
  )

  const dealOptions = useMemo(
    () =>
      dealsData?.salesforce_deals.map((d) => ({
        value: String(d.id),
        label: d.salesforce_deal_name,
        hasAno: d.has_ano,
        hasInquiry: d.has_inquiry,
      })) ?? [],
    [dealsData],
  )

  const selectDeal = (deal: SalesforceDeal | SelectedDeal) => {
    setSelectedId(deal.id)
    setSelectedDeal({
      id: deal.id,
      salesforce_deal_name: deal.salesforce_deal_name,
      salesforce_customer_id: deal.salesforce_customer_id,
      customer_name: deal.customer_name,
    })
  }

  const handleRowClick = (deal: SalesforceDeal) => {
    selectDeal(deal)
  }

  const handleDealChange = (dealId: string) => {
    const deal = dealsData?.salesforce_deals.find((d) => d.id === Number(dealId))
    if (deal) {
      selectDeal(deal)
    }
  }

  // Step 2: 引合情報入力
  const [anoProjectId, setAnoProjectId] = useState('')
  const [overview, setOverview] = useState('')
  const [requirements, setRequirements] = useState('')
  const [contactId, setContactId] = useState('')
  const [coordinationMemberId, setCoordinationMemberId] = useState('')
  const [primaryDivisionId, setPrimaryDivisionId] = useState('')
  const [salesMemberId, setSalesMemberId] = useState('')
  const [serviceManagerMemberId, setServiceManagerMemberId] = useState('')
  const [startOn, setStartOn] = useState('')
  const [isStartAsap, setIsStartAsap] = useState(false)
  const [endOn, setEndOn] = useState('')
  const [estimatedAmount, setEstimatedAmount] = useState('')
  const [industryName, setIndustryName] = useState('')
  const [station, setStation] = useState('')
  const [workLocations, setWorkLocations] = useState<WorkLocations>({ ...DEFAULT_WORK_LOCATIONS })
  const [priority, setPriority] = useState('')
  const [recruitmentType, setRecruitmentType] = useState<InquiryRecruitmentType | ''>('')

  // ANOフェッチ（step 2 遷移時のみ）
  const { data: anosData, isLoading: isLoadingAnos } = useSalesforceDealAnos(
    step === 'input-details' ? selectedDeal?.id : undefined,
  )

  // ANO選択時にプロジェクトメンバーを取得
  const { data: projectMembersData } = useSalesforceProjectMembers(anoProjectId ? Number(anoProjectId) : null)

  // 取引先責任者フェッチ
  const { data: contactsData } = useContacts(selectedDeal?.salesforce_customer_id)
  const contactOptions =
    contactsData?.contacts.map((c) => ({
      value: String(c.id),
      label: c.name,
    })) ?? []

  // ロールメンバーフェッチ
  const { data: coordinationMembersData } = useInquiryRoleMembers({
    isManager: true,
    enabled: step === 'input-details',
  })
  const coordinationMemberOptions: AppComboboxOption[] = useMemo(
    () =>
      coordinationMembersData?.members.map((m) => ({
        value: String(m.id),
        label: m.name,
        data: { employeeNumber: m.employeeNumber },
      })) ?? [],
    [coordinationMembersData],
  )

  const { data: salesMembersData } = useInquiryRoleMembers({ isSales: true, enabled: step === 'input-details' })
  const salesMemberOptions: AppComboboxOption[] = useMemo(
    () =>
      salesMembersData?.members.map((m) => ({
        value: String(m.id),
        label: m.name,
        data: { employeeNumber: m.employeeNumber },
      })) ?? [],
    [salesMembersData],
  )

  const { data: smgMembersData } = useInquiryRoleMembers({ isSmg: true, enabled: step === 'input-details' })
  const smgMemberOptions: AppComboboxOption[] = useMemo(
    () =>
      smgMembersData?.members.map((m) => ({
        value: String(m.id),
        label: m.name,
        data: { employeeNumber: m.employeeNumber },
      })) ?? [],
    [smgMembersData],
  )

  // 部門フェッチ
  const { divisionOptions } = useDivisionOptions({ isEnabled: true, isAssignable: true })

  // ANOオプション（rightItem のレンダリングはコンポーネント側で行う）
  const anos = anosData?.anos ?? []

  // Mutation
  const { mutateAsync: createInquiryMutation, isPending } = useCreateInquiry()

  // ANO/商談に依存するフィールドのリセット
  const resetAnoFields = () => {
    setAnoProjectId('')
    setSalesMemberId('')
    setServiceManagerMemberId('')
    setStartOn('')
    setIsStartAsap(false)
    setEndOn('')
    setEstimatedAmount('')
    setIndustryName('')
    setStation('')
    setWorkLocations({ ...DEFAULT_WORK_LOCATIONS })
    setPriority('')
    setRecruitmentType('')
  }

  const resetDealSelection = () => {
    setDealName('')
    setSelectedId(null)
    setSelectedDeal(null)
  }

  const resetDealFields = () => {
    resetDealSelection()
    resetAnoFields()
  }

  const resetInputFields = () => {
    setContactId('')
    resetAnoFields()
    setOverview('')
    setRequirements('')
    setCoordinationMemberId('')
    setPrimaryDivisionId('')
    setTouched({})
  }

  const handleCustomerChange = (value: string) => {
    const customer = customersData?.customers.find((c) => String(c.id) === value)
    setCustomerId(value)
    setCustomerName(customer?.name ?? '')
    setContactId('')
    resetDealFields()
  }

  // デバウンス内で参照する最新の選択リセット処理。onChange/resetDealFields は毎レンダー再生成されるため、
  // effect の依存配列には入れず ref 経由で参照してタイマーの再設定を防ぐ。
  const latestRef = useRef({ customerQuery, resetCustomerSelection: () => {} })
  latestRef.current = {
    customerQuery,
    resetCustomerSelection: () => {
      if (customerId === '') return
      setCustomerId('')
      setCustomerName('')
      setContactId('')
      resetDealFields()
    },
  }

  // 入力停止から 300ms 後に検索クエリを確定して API を発火する（デバウンス）。
  // 顧客は数万件規模あるため入力のたびに検索すると過剰な API 発火になる。
  useEffect(() => {
    const trimmed = customerSearchInput.trim()
    if (trimmed === latestRef.current.customerQuery) return
    const timerId = setTimeout(() => {
      setCustomerQuery(trimmed)
      latestRef.current.resetCustomerSelection()
    }, 300)
    return () => clearTimeout(timerId)
  }, [customerSearchInput])

  const handleClose = () => {
    onOpenChange(false)
    setStep('select-deal')
    setCustomerId('')
    setCustomerName('')
    setCustomerSearchInput('')
    setCustomerQuery('')
    resetDealSelection()
    resetInputFields()
  }

  const handleNext = () => {
    if (!selectedDeal) return
    setStep('input-details')
  }

  const handleBack = () => {
    setStep('select-deal')
    resetInputFields()
  }

  // 商談変更時にANO関連をリセット
  const handleDealChangeInStep2 = (dealId: string) => {
    if (dealId) {
      handleDealChange(dealId)
      resetAnoFields()
    } else {
      resetDealFields()
    }
    setTouched({ dealId: true })
  }

  const hasEnabledAnos = anos.some((a) => a.enabled)

  // 毎レンダーでエラーを計算
  const allErrors = useMemo(() => {
    const { errors } = validateForm(inquiryFormSchema, {
      overview: overview || null,
      requirements: requirements,
      startOn: resolveEffectiveStartOn(startOn, isStartAsap),
      endOn: endOn || null,
      estimatedAmount: estimatedAmount ? Number(estimatedAmount) : null,
      industryName: industryName || null,
      station: station || null,
      priority: (priority as InquiryPriority) || undefined,
      recruitmentType: recruitmentType || null,
      inquiryCloseReason: null,
    })

    const result = errors as Record<string, string>

    if (!selectedId) {
      result.dealId = VALIDATION_MESSAGES.REQUIRED_SELECT
    }

    if (hasEnabledAnos && !anoProjectId) {
      result.anoProjectId = VALIDATION_MESSAGES.REQUIRED_SELECT
    }

    return result
  }, [
    overview,
    requirements,
    startOn,
    endOn,
    isStartAsap,
    estimatedAmount,
    industryName,
    station,
    priority,
    recruitmentType,
    hasEnabledAnos,
    anoProjectId,
    selectedId,
  ])

  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const formErrors = getVisibleErrors(allErrors, touched)
  const hasErrors = Object.keys(formErrors).length > 0

  // フィールド変更時に touched にするラッパー
  const withTouch = <T>(key: string, setter: (v: T) => void) => {
    return (v: T) => {
      setter(v)
      setTouched((prev) => ({ ...prev, [key]: true }))
    }
  }

  const handleRegister = async () => {
    if (!selectedDeal) return
    if (isLoadingAnos) return

    // 全フィールドを touched にしてエラーを表示する
    setTouched(touchAll(inquiryFormSchema, 'anoProjectId'))

    if (Object.keys(allErrors).length > 0) return

    await createInquiryMutation({
      dealId: selectedDeal.id,
      overview: overview.trim() || null,
      requirements: requirements.trim(),
      salesforceProjectId: anoProjectId ? Number(anoProjectId) : null,
      contactId: contactId ? Number(contactId) : null,
      coordinationMemberId: coordinationMemberId ? Number(coordinationMemberId) : null,
      primaryDivisionId: primaryDivisionId ? Number(primaryDivisionId) : null,
      salesMemberId: salesMemberId ? Number(salesMemberId) : null,
      serviceManagerMemberId: serviceManagerMemberId ? Number(serviceManagerMemberId) : null,
      startOn: resolveEffectiveStartOn(startOn, isStartAsap),
      endOn: endOn || null,
      estimatedAmount: estimatedAmount ? Number(estimatedAmount) : null,
      industryName: industryName || null,
      station: station || null,
      isOnsite: workLocations.onsite,
      isOffsite: workLocations.offsite,
      isRemote: workLocations.remote,
      isStartAsap,
      priority: (priority as InquiryPriority) || undefined,
      recruitmentType: recruitmentType || null,
    })

    handleClose()
  }

  // ANOが1件の場合、自動選択（引合登録済みの場合はスキップ）
  useEffect(() => {
    const firstAno = anosData?.anos[0]
    if (anosData && anosData.anos.length === 1 && firstAno && firstAno.enabled && !anoProjectId) {
      setAnoProjectId(String(firstAno.salesforce_project_id))
    }
  }, [anosData, anoProjectId])

  // ANO選択時に営業担当・SMG担当をセット
  useEffect(() => {
    if (!anoProjectId) return

    if (!projectMembersData) {
      setSalesMemberId('')
      setServiceManagerMemberId('')
      return
    }

    const members = projectMembersData.members
    const salesMember = members.find(
      (m) => m.salesforceProjectMemberTypeId === SALESFORCE_PROJECT_MEMBER_TYPE_IDS.SALES_MEMBER,
    )
    const smgMember = members.find(
      (m) => m.salesforceProjectMemberTypeId === SALESFORCE_PROJECT_MEMBER_TYPE_IDS.SERVICE_MANAGER,
    )
    setSalesMemberId(salesMember ? String(salesMember.memberId) : '')
    setServiceManagerMemberId(smgMember ? String(smgMember.memberId) : '')
  }, [anoProjectId, projectMembersData])

  return {
    // Step
    step,

    // Step 1: 商談選択
    customerId,
    customerName,
    customerSearchInput,
    setCustomerSearchInput,
    customerQuery,
    isFetchingCustomers,
    dealName,
    setDealName,
    selectedId,
    selectedDeal,
    customersData,
    dealResults,
    dealOptions,
    handleCustomerChange,
    handleRowClick,
    handleDealChangeInStep2,

    // Step 2: 引合情報入力
    anoProjectId,
    setAnoProjectId: withTouch('anoProjectId', setAnoProjectId),
    overview,
    setOverview: withTouch('overview', setOverview),
    requirements,
    setRequirements: withTouch('requirements', setRequirements),
    contactId,
    setContactId,
    coordinationMemberId,
    setCoordinationMemberId,
    primaryDivisionId,
    setPrimaryDivisionId,
    salesMemberId,
    setSalesMemberId,
    serviceManagerMemberId,
    setServiceManagerMemberId,
    startOn,
    setStartOn: withTouch('startOn', setStartOn),
    isStartAsap,
    setIsStartAsap,
    endOn,
    setEndOn: withTouch('endOn', setEndOn),
    estimatedAmount,
    setEstimatedAmount: withTouch('estimatedAmount', setEstimatedAmount),
    industryName,
    setIndustryName: withTouch('industryName', setIndustryName),
    station,
    setStation: withTouch('station', setStation),
    workLocations,
    setWorkLocations,
    priority,
    setPriority,
    recruitmentType,
    setRecruitmentType,

    // Options
    anos,
    hasEnabledAnos,
    isLoadingAnos,
    contactOptions,
    coordinationMemberOptions,
    salesMemberOptions,
    smgMemberOptions,
    divisionOptions,

    // Validation
    formErrors,
    hasErrors,

    // Actions
    isPending,
    handleClose,
    handleNext,
    handleBack,
    handleRegister,
  }
}
